import React from "react";
import { Field } from "formik";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { CheckListIcon } from "@components/Icons";
import CnpjCpfField from "@components/CnpjCpfField";

import { useUserForm } from "../hook/useUserForm";
import { CNPJTable } from "./CNPJTable";
import {
  STextField,
  addButtonWrapperSx,
  carrierBoxSx,
  carrierSearchButtonWrapperSx,
  checklistIconStyle,
  cnpjSectionSx,
  containerSx,
  footerActionsSx,
  getBtnTextSx,
  getProfileLabelSx,
  headerTitleSx,
  profileFieldWrapperSx,
  profileSelectSx,
  spacerBoxSx,
  submitButtonSx,
} from "./UserForm.style";

interface UserFormProps {
  loading: boolean;
  title: string;
  submitLabel: string;
  submitLoadingLabel: string;
  onCancel: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
  loading,
  title,
  submitLabel,
  submitLoadingLabel,
  onCancel,
}) => {
  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleChange,
    handleBlur,
    handleProfileChange,
    handleAddCNPJ,
    handleSearchBPID,
    handleRemoveCNPJ,
    profileOptions,
    isCarrierProfile,
    isDriverBPIDValid,
    isGroupCNPJEmpty,
    isCNPJFieldsDisabled,
    isFormInvalid,
    isLoading,
  } = useUserForm();

  const bpidField = (label: string) => (
    <STextField
      name="bp"
      label={label}
      variant="outlined"
      value={values.attributes?.BPID}
      onChange={handleChange("attributes.BPID")}
      onBlur={handleBlur("attributes.BPID")}
      error={touched.attributes?.BPID && !!errors.attributes?.BPID}
      helperText={touched.attributes?.BPID && errors.attributes?.BPID}
    />
  );

  return (
    <Grid container spacing={3} sx={containerSx}>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        item
        xs={12}
      >
        <Typography sx={headerTitleSx}>{title}</Typography>
        <CheckListIcon style={checklistIconStyle} />
      </Grid>

      <Grid item xs={12} md={6}>
        <Grid container spacing={{ xs: 2, sm: 4 }}>
          <Grid
            item
            xs={12}
            {...(isDriverBPIDValid && { md: 6 })}
            sx={profileFieldWrapperSx}
          >
            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="profile"
                sx={getProfileLabelSx(!!touched.profile)}
              >
                Tipo de usuário
              </InputLabel>

              <Field
                name="profile"
                label="Perfil"
                as={Select}
                fullWidth
                error={touched.profile && !!errors.profile}
                IconComponent={KeyboardArrowDownIcon}
                sx={profileSelectSx}
                onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                  handleProfileChange(e.target.value)
                }
              >
                {profileOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Field>

              {touched.profile && errors.profile && (
                <Typography variant="caption" color="error">
                  {errors.profile}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {isDriverBPIDValid && (
            <Grid item xs={12} md={6} sx={profileFieldWrapperSx}>
              {bpidField("BP do motorista")}
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <STextField
              name="firstName"
              label="Primeiro nome"
              variant="outlined"
              value={values.firstName}
              onChange={handleChange("firstName")}
              onBlur={handleBlur("firstName")}
              error={touched.firstName && !!errors.firstName}
              helperText={touched.firstName && errors.firstName}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <STextField
              name="lastName"
              label="Sobrenome"
              variant="outlined"
              value={values.lastName}
              onChange={handleChange("lastName")}
              onBlur={handleBlur("lastName")}
              error={touched.lastName && !!errors.lastName}
              helperText={touched.lastName && errors.lastName}
            />
          </Grid>

          <Grid item xs={12}>
            <STextField
              name="email"
              label="E-mail"
              variant="outlined"
              value={values.email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
            />
          </Grid>

          {isCarrierProfile && (
            <Grid item xs={12} sx={carrierBoxSx}>
              <Grid item xs={12} sx={{ mt: 2 }}>
                {bpidField("BP da transportadora")}
              </Grid>

              <Box sx={spacerBoxSx} />

              <Grid item xs={12}>
                <STextField
                  name="razaoSocial"
                  label={isLoading ? "Pesquisando..." : "Razão Social"}
                  variant="outlined"
                  value={values.attributes.INTERNAL_NAME}
                  disabled
                />
              </Grid>

              <Grid item xs={12} sx={carrierSearchButtonWrapperSx}>
                <Button
                  onClick={handleSearchBPID}
                  disabled={!values.attributes?.BPID || isLoading}
                >
                  <Tooltip
                    title={
                      !isGroupCNPJEmpty
                        ? "Novos CNPJs encontrados serão adicionados à lista existente"
                        : ""
                    }
                  >
                    <Typography
                      sx={getBtnTextSx(!values.attributes?.BPID || isLoading)}
                    >
                      {isLoading ? "Pesquisando..." : "Pesquisar CNPJs"}
                    </Typography>
                  </Tooltip>
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={{ xs: 2, sm: 4 }} sx={cnpjSectionSx}>
          <Grid item xs={12} md={6}>
            <CnpjCpfField
              name="CNPJ"
              label="CNPJ ou CPF"
              disabled={isCNPJFieldsDisabled}
              value={values.CNPJ || ""}
              onChange={(raw) => setFieldValue("CNPJ", raw)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <STextField
              disabled={isCNPJFieldsDisabled}
              name="companyName"
              variant="outlined"
              label="Razão Social"
              value={values.companyName}
              onChange={handleChange("companyName")}
            />
          </Grid>

          <Grid item xs={12} sx={addButtonWrapperSx}>
            <Button onClick={handleAddCNPJ} disabled={isCNPJFieldsDisabled}>
              <Typography sx={getBtnTextSx(isCNPJFieldsDisabled)}>
                + Adicionar
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} md={6}>
        <CNPJTable
          items={values.attributes.CNPJ}
          onRemove={handleRemoveCNPJ}
          isEmpty={isGroupCNPJEmpty}
          isLoading={isLoading}
          isCarrierProfile={isCarrierProfile}
        />
      </Grid>

      <Grid item xs={12} sx={footerActionsSx}>
        <Button onClick={onCancel} variant="text" color="primary" size="large">
          Cancelar
        </Button>

        <Button
          variant="contained"
          color="primary"
          size="large"
          type="submit"
          disabled={isFormInvalid}
          sx={submitButtonSx}
          endIcon={loading && <CircularProgress color="secondary" size={20} />}
        >
          {loading ? submitLoadingLabel : submitLabel}
        </Button>
      </Grid>
    </Grid>
  );
};

export default UserForm;
